import type { FC } from 'react';
import React, { useState } from 'react';

import styled from '@emotion/styled';

import { COLORS, SERIF_FONT_FAMILY, Text, mq } from '@clutter/clean';

import boxesImg from '@root/components/landing_pages/smart_storage_v2/images/boxes.png';
import clockAndPhoneImg from '@root/components/landing_pages/smart_storage_v2/images/clock_and_phone.png';
import storageUnitImg from '@root/components/landing_pages/smart_storage_v2/images/storage_unit.png';

const SLIDES = [
  {
    title: 'Pack up and pick up',
    blurb:
      'You pack or we pack! Our pros handle every item with care for a seamless, on-time pickup.',
    illustration: boxesImg,
  },
  {
    title: 'VIP vault',
    blurb:
      'With low damage rates and 24/7 monitoring, your items stay secure in our high tech facilities - big upgrade!',
    illustration: storageUnitImg,
  },
  {
    title: 'Digital-first inventory ',
    blurb:
      'Even from the couch, one click in your online account portal summons whatever you need back to your door.',
    illustration: clockAndPhoneImg,
  },
] as const;

const Container = styled.div`
  background: ${COLORS.cloud};
  position: relative;
  z-index: 2;
`;

const Content = styled.div<{ contentPadding?: string[] }>`
  width: 100%;
  margin: 0 auto;
  ${({ contentPadding }) =>
    contentPadding
      ? mq({ padding: contentPadding })
      : `max-width: 1280px; ${mq({ padding: ['48px 24px', '60px 80px'] })}`}
`;

const ExplainerContainer = styled.div<{ alignStart?: boolean }>`
  display: flex;

  ${({ alignStart }) =>
    mq({
      flexDirection: ['column', null, 'row'],
      alignItems: ['initial', null, 'center'],
      gap: ['24px', null, alignStart ? '0px' : '115px'],
      justifyContent: [
        'initial',
        null,
        alignStart ? 'space-between' : 'center',
      ],
    })}
`;

const ExplainerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex-shrink: 0;

  ${mq({
    order: [2, null, 1],
    width: ['100%', null, '376px'],
  })}
`;

const ExplainerTextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ExplainerPhoto = styled.img`
  object-fit: cover;
  ${mq({
    width: ['100%', null, '600px'],
    height: ['233px', null, '380px'],
    order: [1, null, 2],
    borderRadius: ['25px', null, '36px'],
  })}
`;

const SectionLabel = styled.p`
  color: #4e4e4e;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-family: 'Basier Square';
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  margin: 0;
`;

const SectionTitle = styled.h2`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 500;
  margin: 0;
  font-size: 40px;
  line-height: 1.1;
`;

const SectionText = styled(Text.Body)`
  line-height: 1.2;
  margin: 0;
  font-size: 20px;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 36px;
`;

const NavigationButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  color: ${COLORS.cloud};
  background: ${COLORS.tealJungle};

  &:disabled {
    color: ${COLORS.hippo};
    background: #e4e4e4;
    cursor: default;
  }
`;

const NextChevron: FC<{ fill?: string }> = ({ fill = COLORS.cloud }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="9"
    height="15"
    viewBox="0 0 9 15"
    fill="none"
  >
    <path
      d="M1.67988 14.4L8.87988 7.2L1.67988 0L-0.000117302 1.68L5.51988 7.2L-0.000117302 12.72L1.67988 14.4Z"
      fill={fill}
    />
  </svg>
);

const PrevChevron: FC<{ fill?: string }> = ({ fill = COLORS.hippo }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="9"
    height="15"
    viewBox="0 0 9 15"
    fill="none"
  >
    <path
      d="M7.2 14.4L0 7.2L7.2 0L8.88 1.68L3.36 7.2L8.88 12.72L7.2 14.4Z"
      fill={fill}
    />
  </svg>
);

const Navigation: FC<{
  count: number;
  index: number;
  onNavigate(_: (current: number) => number): void;
}> = ({ count, index, onNavigate }) => {
  const next = index < count - 1;
  const prev = index > 0;

  const onNext = () => {
    onNavigate((current) => {
      current++;
      return current >= count ? count - 1 : current;
    });
  };

  const onPrev = () => {
    onNavigate((current) => {
      current--;
      return current < 0 ? 0 : current;
    });
  };

  return (
    <NavigationButtons>
      <NavigationButton aria-label="Prev" onClick={onPrev} disabled={!prev}>
        <PrevChevron fill={prev ? COLORS.cloud : COLORS.hippo} />
      </NavigationButton>
      <NavigationButton aria-label="Next" onClick={onNext} disabled={!next}>
        <NextChevron fill={next ? COLORS.cloud : COLORS.hippo} />
      </NavigationButton>
    </NavigationButtons>
  );
};

export const HowItWorks: FC<{
  contentPadding?: string[];
  alignStart?: boolean;
}> = ({ contentPadding, alignStart }) => {
  const [index, setIndex] = useState(0);
  const count = SLIDES.length;
  const current = SLIDES[index];

  return (
    <Container id="how_it_works">
      <Content contentPadding={contentPadding}>
        <ExplainerContainer alignStart={alignStart}>
          <ExplainerContent>
            <ExplainerTextArea>
              <SectionLabel>How does Clutter work?</SectionLabel>
              <SectionTitle>{current.title}</SectionTitle>
              <SectionText>{current.blurb}</SectionText>
            </ExplainerTextArea>
            <Navigation count={count} index={index} onNavigate={setIndex} />
          </ExplainerContent>
          <ExplainerPhoto
            src={current.illustration}
            alt={current.title}
            width={1024}
            height={1024}
          />
        </ExplainerContainer>
      </Content>
    </Container>
  );
};
